import React, { useCallback } from 'react';
import TextField from '@atlaskit/textfield';
import { ObjectDetailsSpacer } from '@atlassian/jira-servicedesk-insight-attribute-views/src/common/ui/styled.tsx';
import { TextReadView } from '@atlassian/jira-servicedesk-insight-attribute-views/src/ui/text/index.tsx';
import {
	useDefaultMultipleAttributeValuesAsString,
	getDefaultMultipleAttributeValuesAsString,
} from '@atlassian/jira-servicedesk-insight-object-attribute-utils/src/utils/default/index.tsx';
import { splitValuesOnSpace } from '@atlassian/jira-servicedesk-insight-object-attribute-utils/src/utils/text/index.tsx';
import { useEditableAttribute } from '@atlassian/jira-servicedesk-insight-object-details-store/src/controllers/use-editable-attribute/index.tsx';
import type { DefaultAttributeType } from '@atlassian/jira-servicedesk-insight-object-types/src/common/types/object-attribute-type.tsx';
import { defaultTypesById } from '@atlassian/jira-servicedesk-insight-object-types/src/common/utils/type-matchers/index.tsx';
import {
	InlineEditableAttribute,
	InlineValidationErrorID,
} from '../../common/ui/editable-attribute/index.tsx';

export type Props = {
	isEditable: boolean;
	attributeType: DefaultAttributeType;
};

export const TextAttribute = (props: Props) => {
	const { attributeType, isEditable } = props;

	const [{ attribute }] = useEditableAttribute(attributeType);
	const [value, setValue] = useDefaultMultipleAttributeValuesAsString(attribute);

	const resetValue = useCallback(() => {
		setValue(getDefaultMultipleAttributeValuesAsString(attribute));
	}, [attribute, setValue]);

	const onValueChange = useCallback(
		(event: React.ChangeEvent<HTMLInputElement>) => {
			setValue(event.target.value);
		},
		[setValue],
	);

	const defaultTypeId = attributeType.defaultType.id;
	// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
	const defaultTypeName = defaultTypesById[defaultTypeId as keyof typeof defaultTypesById];

	const transformValue = useCallback(
		(values: string) => {
			if (defaultTypeName === 'url' || defaultTypeName === 'email') {
				return splitValuesOnSpace([values]);
			}
			return values;
		},
		[defaultTypeName],
	);

	const transformedValue = transformValue(value);

	return (
		<InlineEditableAttribute
			testId="servicedesk-insight-inline-editable-attributes.ui.text-attribute"
			value={transformedValue}
			onChange={onValueChange}
			storedValue={getDefaultMultipleAttributeValuesAsString(attribute)}
			isEditable={isEditable}
			attributeType={attributeType}
			hideActionButtons={false}
			renderReadView={() => (
				<ObjectDetailsSpacer>
					<TextReadView value={transformedValue} defaultAttributeType={attributeType} />
				</ObjectDetailsSpacer>
			)}
			renderEditView={({ onChange, ...fieldProps }) => (
				<TextField
					{...fieldProps}
					value={value}
					autoFocus
					onChange={onChange}
					{...(fieldProps.hasValidationError
						? { 'aria-describedby': InlineValidationErrorID }
						: {})}
					isInvalid={fieldProps.hasValidationError}
				/>
			)}
			onResetValue={resetValue}
		/>
	);
};
